import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import MondayIntegrationFlow from './MondayIntegrationFlow';
import CRMHelpGuide from './CRMHelpGuide';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';

const CRMContainer = styled.div`
  background: rgba(99, 102, 241, 0.05);
  border: none;
  border-radius: 12px;
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding: 24px;
  margin-bottom: 20px;
  backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  padding: 10px 18px;
  display: inline-block;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  letter-spacing: 0.3px;
`;

// Removed unused CRMCard component

const CRMOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.isActive 
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)' 
    : 'rgba(255, 255, 255, 0.02)'};
  border-color: ${props => props.isActive ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'};
  backdrop-filter: blur(8px);

  &:hover {
    background: rgba(99, 102, 241, 0.1);
    border-color: rgba(99, 102, 241, 0.4);
    transform: translateY(-1px);
  }
`;

const CRMInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CRMLogo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${props => props.bgColor || '#4CAF50'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
`;

const CRMDetails = styled.div`
  flex: 1;
`;

const CRMName = styled.div`
  color: #FFFFFF;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
`;

const CRMDescription = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
`;

const CRMStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${props => props.isConnected ? '#4CAF50' : 'rgba(255, 255, 255, 0.5)'};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.isConnected ? '#4CAF50' : 'rgba(255, 255, 255, 0.3)'};
`;

const Button = styled.button`
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' 
    : 'rgba(99, 102, 241, 0.15)'};
  color: #fff;
  border: ${props => props.variant === 'primary' ? 'none' : '1px solid rgba(99, 102, 241, 0.3)'};
  border-radius: 8px;
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  box-shadow: ${props => props.variant === 'primary' ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'};
  
  &:hover {
    background: ${props => props.variant === 'primary' 
      ? 'linear-gradient(135deg, #5558e3 0%, #7c3aed 100%)' 
      : 'rgba(99, 102, 241, 0.25)'};
    transform: translateY(-1px);
    box-shadow: ${props => props.variant === 'primary' ? '0 6px 20px rgba(99, 102, 241, 0.4)' : 'none'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ConnectionSummary = styled.div`
  background-color: rgba(47, 136, 245, 0.1);
  border: 1px solid rgba(47, 136, 245, 0.3);
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;
`;

const SummaryTitle = styled.div`
  color: #FFFFFF;
  font-weight: 600;
  margin-bottom: 8px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  margin-bottom: 4px;
`;

// Available CRM options
const CRM_OPTIONS = [
  {
    id: 'monday',
    name: 'Monday.com',
    description: 'Sync leads to your Monday.com boards',
    bgColor: '#ff3d71',
    isActive: true,
    isAvailable: true
  },
  {
    id: 'salesforce',
    name: 'Salesforce',
    description: 'Enterprise CRM integration (Coming Soon)',
    bgColor: '#00a1e0',
    isActive: false,
    isAvailable: false
  },
  {
    id: 'hubspot',
    name: 'HubSpot',
    description: 'All-in-one CRM and marketing platform (Coming Soon)',
    bgColor: '#ff7a59',
    isActive: false,
    isAvailable: false
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    description: 'Simple and effective sales CRM (Coming Soon)',
    bgColor: '#1f5582',
    isActive: false,
    isAvailable: false
  }
];

const CRMIntegration = () => {
  const apiClient = useAuthenticatedAxios();
  const [showMondayFlow, setShowMondayFlow] = useState(false);
  const [showHelpGuide, setShowHelpGuide] = useState(false);
  const [crmConnections, setCrmConnections] = useState({});
  const [profileCRM, setProfileCRM] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch CRM data from profile API
    const fetchCRMData = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/profile');
        const profileData = response.data.profile || {};

        // Check if CRM exists in profile
        if (profileData.crm) {
          setProfileCRM(profileData.crm);

          // Map CRM type to connection format
          const crmType = profileData.crm.type?.toLowerCase();
          if (crmType === 'monday') {
            const mondayConnection = {
              apiKey: profileData.crm.key,
              boardId: profileData.crm.board_id,
              connectedAt: new Date().toISOString(),
              status: 'connected',
              source: 'api' // Mark as coming from API
            };

            setCrmConnections({
              monday: mondayConnection
            });
          }
        }
      } catch (error) {
        console.error('Error fetching CRM data from profile:', error);

        // Fall back to localStorage if API fails
        try {
          const stored = localStorage.getItem('crm_connections');
          if (stored) {
            setCrmConnections(JSON.parse(stored));
          }
        } catch (storageError) {
          console.error('Error loading CRM connections from localStorage:', error);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCRMData();
  }, [apiClient]);

  const handleCRMSelect = (crmOption) => {
    if (!crmOption.isAvailable) return;
    
    if (crmOption.id === 'monday') {
      setShowMondayFlow(true);
    }
  };

  const handleMondayConnectionComplete = (connectionData) => {
    const newConnections = {
      ...crmConnections,
      monday: {
        ...connectionData,
        connectedAt: new Date().toISOString(),
        status: 'connected'
      }
    };
    
    setCrmConnections(newConnections);
    
    // Save to localStorage
    try {
      localStorage.setItem('crm_connections', JSON.stringify(newConnections));
    } catch (error) {
      console.error('Error saving CRM connections:', error);
    }
    
    setShowMondayFlow(false);
  };

  const handleDisconnectCRM = async (crmId) => {
    // Clear the backend config first — otherwise the server keeps the saved
    // crm credentials and continues syncing leads even after "disconnecting".
    try {
      await apiClient.delete('/profile/crm');
      setProfileCRM(null);
    } catch (error) {
      console.error('Error disconnecting CRM on server:', error);
      // Surface the failure so the user knows the disconnect didn't persist.
      alert('Failed to disconnect CRM. Please try again.');
      return;
    }

    const newConnections = { ...crmConnections };
    delete newConnections[crmId];

    setCrmConnections(newConnections);

    try {
      localStorage.setItem('crm_connections', JSON.stringify(newConnections));
    } catch (error) {
      console.error('Error saving CRM connections:', error);
    }
  };

  const isConnected = (crmId) => {
    return crmConnections[crmId]?.status === 'connected';
  };

  return (
    <CRMContainer>
      <SectionTitle>CRM Integration</SectionTitle>

      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '16px' }}>
          Connect your CRM to sync leads from chat conversations.
        </p>

        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
          <Button
            variant="secondary"
            onClick={() => setShowHelpGuide(true)}
          >
            📚 Setup Guide
          </Button>
        </div>
      </div>

      {loading && (
        <div style={{
          color: 'rgba(255, 255, 255, 0.7)',
          padding: '12px',
          textAlign: 'center',
          marginBottom: '16px'
        }}>
          Loading CRM connections...
        </div>
      )}

      {/* CRM Options */}
      <div>
        {CRM_OPTIONS.map(option => (
          <CRMOption 
            key={option.id}
            isActive={option.isActive && option.isAvailable}
            onClick={() => handleCRMSelect(option)}
            style={{ 
              cursor: option.isAvailable ? 'pointer' : 'not-allowed',
              opacity: option.isAvailable ? 1 : 0.6 
            }}
          >
            <CRMInfo>
              <CRMLogo bgColor={option.bgColor}>
                {option.name.charAt(0)}
              </CRMLogo>
              <CRMDetails>
                <CRMName>{option.name}</CRMName>
                <CRMDescription>{option.description}</CRMDescription>
              </CRMDetails>
            </CRMInfo>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <CRMStatus isConnected={isConnected(option.id)}>
                <StatusDot isConnected={isConnected(option.id)} />
                {isConnected(option.id) ? 'Connected' : option.isAvailable ? 'Available' : 'Coming Soon'}
              </CRMStatus>
              
              {option.isAvailable && (
                <div>
                  {isConnected(option.id) ? (
                    <Button 
                      variant="secondary" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDisconnectCRM(option.id);
                      }}
                    >
                      Disconnect
                    </Button>
                  ) : (
                    <Button variant="primary">
                      Connect
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CRMOption>
        ))}
      </div>

      {/* Connection Summary */}
      {Object.keys(crmConnections).length > 0 && (
        <ConnectionSummary>
          <SummaryTitle>Active Connections</SummaryTitle>
          {Object.entries(crmConnections).map(([crmId, connection]) => (
            <div key={crmId}>
              <SummaryItem>
                <span>{CRM_OPTIONS.find(opt => opt.id === crmId)?.name}</span>
                <span style={{ color: '#4CAF50' }}>✓ Connected</span>
              </SummaryItem>
              {connection.source === 'api' && (
                <SummaryItem>
                  <span>Source:</span>
                  <span style={{
                    backgroundColor: 'rgba(47, 136, 245, 0.2)',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    API Profile
                  </span>
                </SummaryItem>
              )}
              {connection.boardId && (
                <SummaryItem>
                  <span>Board ID:</span>
                  <span>{connection.boardId}</span>
                </SummaryItem>
              )}
              {connection.boardName && (
                <SummaryItem>
                  <span>Board Name:</span>
                  <span>{connection.boardName}</span>
                </SummaryItem>
              )}
              <SummaryItem>
                <span>Connected:</span>
                <span>{new Date(connection.connectedAt).toLocaleDateString()}</span>
              </SummaryItem>
            </div>
          ))}
        </ConnectionSummary>
      )}

      {/* Monday.com Integration Flow Modal */}
      {showMondayFlow && (
        <MondayIntegrationFlow
          onComplete={handleMondayConnectionComplete}
          onCancel={() => setShowMondayFlow(false)}
        />
      )}

      {/* Help Guide Modal */}
      {showHelpGuide && (
        <CRMHelpGuide
          onClose={() => setShowHelpGuide(false)}
        />
      )}
    </CRMContainer>
  );
};

export default CRMIntegration;
